<template>
  <SButton
    v-if="row.isRecurring"
    variant="outline"
    size="lg"
    class="w-full cursor-pointer disabled:cursor-not-allowed"
    data-cy="product-select-button-recurring"
    :disabled="!ctx.canSell.value"
    @click="onRecurringClick"
  >
    <div :id="`selectButton-${row.id}`"></div>
    {{ $t("button.select") }}
    <span v-if="orderQuantity" class="order-quantity"
      >X{{ orderQuantity }}</span
    >
  </SButton>

  <SButton
    v-else
    variant="outline"
    size="lg"
    class="w-full cursor-pointer disabled:cursor-not-allowed"
    data-cy="product-select-button"
    :disabled="!ctx.allowSelectProduct(row) || !ctx.canSell.value"
    @mousedown="(event: Event) => ctx.refreshPeriod(event, row)"
    @mouseup="(event: Event) => ctx.selectProduct(event, row)"
    @touchstart="(event: Event) => ctx.refreshPeriod(event, row)"
    @touchend="(event: Event) => ctx.selectProduct(event, row)"
  >
    <span v-if="ctx.allowSelectProduct(row)">{{ $t("button.select") }}</span>
    <span v-else>{{ $t("common.not-available") }}</span>
    <span v-if="orderQuantity" class="order-quantity"
      >X{{ orderQuantity }}</span
    >
  </SButton>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { SButton } from "@smeetz/ui-core";

import { useProductsContext } from "../../useProductsContext";
import type { IProductTableData } from "@/types/db/product-category";

type Props = {
  row: IProductTableData;
};

const props = defineProps<Props>();
const ctx = useProductsContext();

/**
 * Order-quantity badge shown next to "Select" — undefined when the order has no
 * entries for this product. Reactive: re-reads when the order changes.
 *
 * @returns {number | undefined}
 */
const orderQuantity = computed((): number | undefined => {
  return ctx.orderQuantitiesByCategory.value[props.row.uuid];
});

/**
 * Recurring product click — fires the same select + openCalendar pair as the
 * legacy template. The selectProduct call is intentionally not awaited; the
 * calendar opens immediately for the user to pick a date.
 *
 * @returns {boolean} Sentinel.
 */
function onRecurringClick(): boolean {
  ctx.selectProduct(null, props.row);
  ctx.openCalendar(props.row.id);

  return true;
}
</script>
